import { useEffect, useState } from "react";
import { finAPI, fmtINR, fmtDate, METHOD_STYLE, STATUS_STYLE } from "./finApi";
import { Plus, X, ReceiptText, Filter } from "lucide-react";

const METHODS = ["cash", "card", "upi", "bank_transfer", "corporate", "online", "wallet"];

function CreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    method: "cash", amount: "", reference_no: "", guest_name: "",
    department: "", notes: "", folio_id: "", reservation_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await finAPI.createPayment(body);
      onCreated();
      onClose();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fin-create-payment-dialog">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Record Payment</h3>
          <button onClick={onClose} data-testid="fin-payment-close"><X className="w-5 h-5 text-slate-400 hover:text-slate-700"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Method *</label>
              <select value={form.method} onChange={e => setForm({...form, method: e.target.value})}
                data-testid="fin-payment-method"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                {METHODS.map(m => <option key={m} value={m}>{METHOD_STYLE[m]?.label || m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Amount (₹) *</label>
              <input value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                required pattern="^\d+(\.\d{1,2})?$" placeholder="1500.00"
                data-testid="fin-payment-amount"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Guest Name</label>
              <input value={form.guest_name} onChange={e => setForm({...form, guest_name: e.target.value})}
                data-testid="fin-payment-guest"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Reference / UTR</label>
              <input value={form.reference_no} onChange={e => setForm({...form, reference_no: e.target.value})}
                data-testid="fin-payment-ref"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Folio ID</label>
              <input value={form.folio_id} onChange={e => setForm({...form, folio_id: e.target.value})}
                data-testid="fin-payment-folio"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Department</label>
              <input value={form.department} onChange={e => setForm({...form, department: e.target.value})}
                data-testid="fin-payment-dept"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              rows={2} data-testid="fin-payment-notes"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </div>
          {err && <div className="text-rose-700 text-[12px] bg-rose-50 px-3 py-2 rounded-lg" data-testid="fin-payment-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] font-medium text-slate-600 hover:text-slate-900">Cancel</button>
            <button type="submit" disabled={busy} data-testid="fin-payment-submit"
              className="px-5 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium hover:brightness-110 disabled:opacity-60">
              {busy ? "Recording…" : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function FinancePayments() {
  const [items, setItems] = useState([]);
  const [filterMethod, setFilterMethod] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (filterMethod) params.method = filterMethod;
    if (filterStatus) params.status = filterStatus;
    finAPI.payments(params).then(setItems).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterMethod, filterStatus]);

  const voidOne = async (p) => {
    const reason = window.prompt("Void reason:");
    if (!reason) return;
    try { await finAPI.voidPayment(p.id, { reason }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fin-payments-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12px] text-slate-500"><Filter className="w-4 h-4"/> Filters:</div>
        <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)}
          data-testid="fin-filter-method"
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
          <option value="">All methods</option>
          {METHODS.map(m => <option key={m} value={m}>{METHOD_STYLE[m]?.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          data-testid="fin-filter-status"
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
          <option value="">All statuses</option>
          <option value="received">Received</option>
          <option value="voided">Voided</option>
        </select>
        <div className="flex-1"/>
        <button onClick={() => setOpen(true)} data-testid="fin-new-payment-btn"
          className="px-4 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium inline-flex items-center gap-2 hover:brightness-110">
          <Plus className="w-4 h-4"/> Record Payment
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center" data-testid="fin-payments-empty">
            <ReceiptText className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-[13px] text-slate-500">No payments match these filters.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">Method</th>
                <th className="text-left px-4 py-2.5">Guest</th>
                <th className="text-left px-4 py-2.5">Reference</th>
                <th className="text-right px-4 py-2.5">Amount</th>
                <th className="text-left px-4 py-2.5">Business Date</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Recorded</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(p => {
                const m = METHOD_STYLE[p.method] || { chip: "bg-slate-100 text-slate-700", label: p.method };
                const s = STATUS_STYLE[p.status] || { chip: "bg-slate-100 text-slate-700", label: p.status };
                return (
                  <tr key={p.id} className="hover:bg-slate-50" data-testid={`fin-payment-row-${p.code}`}>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-hocp-navy">{p.code}</td>
                    <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${m.chip}`}>{m.label}</span></td>
                    <td className="px-4 py-2.5 text-slate-700">{p.guest_name || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-slate-500">{p.reference_no || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-hocp-navy">{fmtINR(p.amount)}</td>
                    <td className="px-4 py-2.5 text-slate-600 tabular-nums">{p.business_date}</td>
                    <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${s.chip}`}>{s.label}</span></td>
                    <td className="px-4 py-2.5 text-slate-500 text-[12px]">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {p.status === "received" && (
                        <button onClick={() => voidOne(p)} data-testid={`fin-void-${p.code}`}
                          className="text-[12px] text-rose-600 hover:text-rose-800 font-medium">Void</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <CreateDialog open={open} onClose={() => setOpen(false)} onCreated={load}/>
    </div>
  );
}
